import { Fragment, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Rating,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  IconButton,
  Switch,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Verified as VerifiedIcon,
  VerifiedUser as VerifiedUserIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  History as HistoryIcon,
  Reply as ReplyIcon,
  Send as SendIcon,
  Sync as SyncIcon,
  VisibilityOff as AnonymousIcon,
  AdminPanelSettings as CommissionerIcon,
  EventAvailable as LineupIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { ratingsApi } from '../api/ratings';
import { adminApi } from '../api/admin';
import { useAuthStore } from '../store/authStore';
import { getErrorMessage } from '../api/client';
import { tokens } from '../theme';

// Reply Form Component
function ReplyForm({ ratingId, onSuccess }: { ratingId: string; onSuccess: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const { user } = useAuthStore();

  const replyMutation = useMutation({
    mutationFn: () => ratingsApi.addReply(ratingId, replyText),
    onSuccess: () => {
      setReplyText('');
      setIsOpen(false);
      onSuccess();
    },
  });

  // Unverified users see the reply affordance, but disabled, with a
  // tooltip pointing them at verification. Surfacing the gated action is
  // the whole point — hiding it would defeat the reminder.
  if (!user?.isVerified) {
    return (
      <Tooltip
        title="Verify your Sleeper account to reply to reviews"
        arrow
        placement="right"
      >
        {/* Wrapping <span> is required for Tooltip to attach listeners
            to a disabled <button> in MUI. */}
        <span>
          <Button
            size="small"
            startIcon={<ReplyIcon />}
            component={RouterLink}
            to="/dashboard?verify=1"
            sx={{ color: 'text.secondary' }}
          >
            Reply
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Box>
      {!isOpen ? (
        <Button
          size="small"
          startIcon={<ReplyIcon />}
          onClick={() => setIsOpen(true)}
          sx={{ color: 'text.secondary' }}
        >
          Reply
        </Button>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            multiline
            maxRows={3}
            placeholder="Write a reply..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            sx={{ flex: 1 }}
            disabled={replyMutation.isPending}
          />
          <IconButton
            color="primary"
            onClick={() => replyMutation.mutate()}
            disabled={!replyText.trim() || replyMutation.isPending}
          >
            <SendIcon />
          </IconButton>
          <Button size="small" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
        </Box>
      )}
      {replyMutation.isError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {getErrorMessage(replyMutation.error)}
        </Typography>
      )}
    </Box>
  );
}

/** Score thresholds for the big-number visual treatment:
 *  - >= ELITE: soft pulsing halo in the accent color.
 *  - displayed as "5.0" (i.e. >= 4.95 after rounding): electric
 *    "crackling" effect with bright white spark peaks.
 *  Anything below ELITE renders flat. */
const ELITE_SCORE_THRESHOLD = 4.8;
const PERFECT_SCORE_THRESHOLD = 4.95;

/**
 * Inline score summary used in the profile reputation cards. Shows
 * the overall average + total count + a per-category breakdown
 * stacked beneath.
 *
 * `accentColor` controls the icon, the big-number color, and the star
 * fill color for both the overall and per-category Ratings — when
 * unset it inherits the theme's secondary (gold) used by manager
 * trust scores. The commissioner block passes `tokens.legendary` so
 * it reads as a more elite/distinct accent than the regular gold.
 *
 * When `overall >= ELITE_SCORE_THRESHOLD`, the big number is wrapped
 * in a layered text-shadow halo + a slow soft pulse animation in the
 * accent color so elite reputations earn a visible "trophy" treatment.
 *
 * When `isEmpty` is true (no reviews yet), the data section renders
 * as a blurred / faded skeleton with `emptyMessage` overlaid on top.
 * The label + icon header stays clear so the user can still tell
 * which kind of score the card represents.
 */
function ScoreBlock({
  label,
  icon,
  overall,
  total,
  categories,
  accentColor,
  isEmpty = false,
  emptyMessage,
}: {
  label: string;
  icon?: React.ReactNode;
  overall: number;
  total: number;
  categories: Array<{ key: string; label: string; value: number }>;
  accentColor?: string;
  isEmpty?: boolean;
  emptyMessage?: string;
}) {
  // Default to MUI's `secondary.main` token (gold). When an explicit
  // hex is provided (commissioner case), apply it everywhere so the
  // whole block reads in the same hue.
  const numberColor = accentColor ?? 'secondary.main';
  // The theme defines a global `iconFilled.color = tokens.gold` override
  // for MuiRating, so setting `color` on the Rating root won't actually
  // tint the stars — the slot selector wins on specificity. We have to
  // target `.MuiRating-iconFilled` directly to override per-instance.
  const ratingSx = accentColor
    ? { '& .MuiRating-iconFilled': { color: accentColor } }
    : undefined;

  // Use the literal hex for the shadow color so we can append alpha
  // bytes — MUI palette tokens won't compose into the textShadow
  // string. Falls back to the brand gold for manager trust scores
  // when no accent was supplied.
  const glowHex = accentColor ?? tokens.gold;
  const isPerfect = overall >= PERFECT_SCORE_THRESHOLD;
  const isElite = !isPerfect && overall >= ELITE_SCORE_THRESHOLD;

  // Soft 3.4s pulse halo. Used for elite-tier (4.8 ≤ score < 5.0).
  const eliteSx = isElite
    ? {
        textShadow: [
          `0 0 8px ${glowHex}cc`,
          `0 0 22px ${glowHex}80`,
          `0 0 40px ${glowHex}33`,
        ].join(', '),
        animation: 'dynastyrep-elite-glow 3.4s ease-in-out infinite',
        '@keyframes dynastyrep-elite-glow': {
          '0%, 100%': {
            textShadow: [
              `0 0 8px ${glowHex}cc`,
              `0 0 22px ${glowHex}80`,
              `0 0 40px ${glowHex}33`,
            ].join(', '),
          },
          '50%': {
            textShadow: [
              `0 0 12px ${glowHex}ee`,
              `0 0 32px ${glowHex}aa`,
              `0 0 56px ${glowHex}55`,
            ].join(', '),
          },
        },
      }
    : {};

  // "Slow electric hum" for a perfect 5.0. Long 5s breathing cycle
  // with smoothly-eased peaks rather than sharp flashes — feels like
  // the steady hum of a power transformer or neon sign rather than
  // crackling lightning. The white inner shadow still appears at the
  // peaks so it reads as "voltage" not just "extra glow", but it
  // fades in/out gradually instead of stabbing.
  const electricSx = isPerfect
    ? {
        // Initial state matches the first keyframe so React doesn't
        // flash a bare number for one frame before the animation kicks in.
        textShadow: [
          `0 0 10px ${glowHex}aa`,
          `0 0 24px ${glowHex}77`,
          `0 0 44px ${glowHex}33`,
        ].join(', '),
        animation: 'dynastyrep-electric-glow 5s ease-in-out infinite',
        '@keyframes dynastyrep-electric-glow': {
          '0%, 100%': {
            textShadow: [
              `0 0 10px ${glowHex}aa`,
              `0 0 24px ${glowHex}77`,
              `0 0 44px ${glowHex}33`,
            ].join(', '),
          },
          // First swell — gentle, soft white core just barely showing.
          '32%': {
            textShadow: [
              `0 0 6px #ffffff55`,
              `0 0 18px ${glowHex}dd`,
              `0 0 36px ${glowHex}aa`,
              `0 0 60px ${glowHex}55`,
            ].join(', '),
          },
          // Bigger swell — wider halo, slightly more white, asymmetric
          // so the rhythm reads as "alive" instead of metronomic.
          '62%': {
            textShadow: [
              `0 0 5px #ffffff88`,
              `0 0 22px ${glowHex}ff`,
              `0 0 42px ${glowHex}cc`,
              `0 0 70px ${glowHex}77`,
            ].join(', '),
          },
          '85%': {
            textShadow: [
              `0 0 12px ${glowHex}cc`,
              `0 0 28px ${glowHex}88`,
              `0 0 48px ${glowHex}44`,
            ].join(', '),
          },
        },
      }
    : {};

  // The data portion of the block (number + stars + breakdown) — kept
  // as a separate node so we can wrap it in a blur-faded shell with an
  // overlay message in the empty state.
  const dataContent = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: numberColor,
            lineHeight: 1,
            fontSize: { xs: 48, md: 56 },
            // Mutually exclusive — perfect overrides elite. The
            // booleans above already enforce that, but we still spread
            // both safely since at most one is non-empty.
            ...eliteSx,
            ...electricSx,
          }}
        >
          {overall.toFixed(1)}
        </Typography>
        <Box>
          <Rating
            value={overall}
            precision={0.1}
            readOnly
            sx={ratingSx}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Based on {total} rating{total === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        {categories.map((cat) => (
          <Box
            key={cat.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              alignItems: 'center',
              columnGap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {cat.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
              {cat.value.toFixed(1)}
            </Typography>
            <Rating
              value={cat.value}
              precision={0.1}
              readOnly
              size="small"
              sx={ratingSx}
            />
          </Box>
        ))}
      </Stack>
    </>
  );

  return (
    <Box>
      {/* Header (label + icon) stays visible in both states so the
          card identity reads even when there's no data yet. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon}
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', letterSpacing: '1.5px' }}
        >
          {label}
        </Typography>
      </Box>

      {isEmpty ? (
        // Empty state: render the same skeleton structure (so cards
        // sitting side by side stay the same height) but blur + fade
        // it, and overlay a centered message explaining why.
        <Box sx={{ position: 'relative' }}>
          <Box
            aria-hidden
            sx={{
              opacity: 0.28,
              filter: 'blur(2px)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {dataContent}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 800,
                letterSpacing: '2.5px',
                color: 'text.secondary',
              }}
            >
              No reviews yet
            </Typography>
            {emptyMessage && (
              <Typography variant="caption" color="text.secondary">
                {emptyMessage}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        dataContent
      )}
    </Box>
  );
}

/** Truncate a string to `max` characters, appending an ellipsis when
 *  cut. Tuned to fit comfortably in the sidebar leagues column at
 *  the md breakpoint. The Teams column was removed so we have a bit
 *  more horizontal room here than before. */
function truncateLeagueName(s: string, max = 24): string {
  if (!s) return s;
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

/**
 * Compact, sortable-feeling table of all leagues for the sidebar.
 *
 * Sorted by season descending so the freshest leagues are at the top;
 * within a season, currently-active leagues (in_season / drafting) sort
 * before pre_draft, which sort before complete. A subtle full-row
 * subheader marks each year transition so the grouping is obvious
 * without making the table heavy.
 *
 * Each row is a router link to the league's detail page.
 */
function LeaguesTable({
  leagues,
}: {
  leagues: Array<{
    id: string;
    name: string;
    season: string;
    status: 'pre_draft' | 'drafting' | 'in_season' | 'complete';
  }>;
}) {
  const statusOrder: Record<typeof leagues[number]['status'], number> = {
    in_season: 0,
    drafting: 0,
    pre_draft: 1,
    complete: 2,
  };
  const sorted = [...leagues].sort((a, b) => {
    const ay = parseInt(a.season, 10);
    const by = parseInt(b.season, 10);
    const aSeason = Number.isNaN(ay) ? -Infinity : ay;
    const bSeason = Number.isNaN(by) ? -Infinity : by;
    if (aSeason !== bSeason) return bSeason - aSeason;
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    return a.name.localeCompare(b.name);
  });

  // Build a lookup so the render loop can decide where to insert a
  // year-section header. We insert above the first row of each season.
  const seasonHeaderInsertedFor = new Set<string>();

  const statusChip = (status: (typeof leagues)[number]['status']) => {
    if (status === 'in_season' || status === 'drafting') {
      return (
        <Chip
          label={status === 'drafting' ? 'Draft' : 'Active'}
          size="small"
          color="success"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      );
    }
    if (status === 'pre_draft') {
      return (
        <Chip
          label="Pre-Draft"
          size="small"
          color="info"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      );
    }
    return (
      <Chip
        label="Done"
        size="small"
        variant="outlined"
        sx={{ height: 20, fontSize: '0.65rem' }}
      />
    );
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>League</TableCell>
            <TableCell align="right" sx={{ pr: 0 }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((league) => {
            const headerKey = league.season || 'Unknown';
            const showHeader = !seasonHeaderInsertedFor.has(headerKey);
            seasonHeaderInsertedFor.add(headerKey);
            return (
              <Fragment key={league.id}>
                {showHeader && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        pl: 0,
                        pt: 1.5,
                        pb: 0.5,
                        border: 'none',
                        fontFamily: '"Barlow Condensed", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        fontSize: 14,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                      }}
                    >
                      {headerKey}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  hover
                  component={RouterLink}
                  to={`/league/${league.id}`}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '& td': { borderBottom: 1, borderColor: 'divider' },
                  }}
                >
                  <TableCell sx={{ pl: 0 }}>
                    <Tooltip
                      title={league.name.length > 24 ? league.name : ''}
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                        }}
                      >
                        {truncateLeagueName(league.name)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    {statusChip(league.status)}
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const ratingCategories = [
  { key: 'payment_reliability', label: 'Payment Reliability' },
  { key: 'communication', label: 'Communication' },
  { key: 'fair_play', label: 'Fair Play' },
  { key: 'league_commitment', label: 'League Commitment' },
  { key: 'fair_trader', label: 'Fair Trader' },
];

// Commissioner-specific rating categories. Mirrors the manager categories'
// shape so the same rendering code can be reused where helpful.
const commissionerRatingCategories = [
  { key: 'fairness', label: 'Fairness' },
  { key: 'rule_enforcement', label: 'Rule Enforcement' },
  { key: 'responsiveness', label: 'Responsiveness' },
  { key: 'communication', label: 'Communication' },
  { key: 'organization', label: 'Organization' },
];

interface SleeperProfilePageProps {
  /**
   * Optional override for the sleeper user id, used by the preview
   * page (`/preview/profile`) to render this component without a real
   * URL match. When provided, takes precedence over `useParams()`.
   */
  sleeperUserIdOverride?: string;
}

export default function SleeperProfilePage({
  sleeperUserIdOverride,
}: SleeperProfilePageProps = {}) {
  const params = useParams<{ sleeperUserId: string }>();
  const sleeperUserId = sleeperUserIdOverride ?? params.sleeperUserId;
  const { isAuthenticated, user } = useAuthStore();
  const queryClient = useQueryClient();

  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [ratingValues, setRatingValues] = useState({
    payment_reliability: 3,
    communication: 3,
    fair_play: 3,
    league_commitment: 3,
    fair_trader: 3,
    review_text: '',
    is_anonymous: false,
  });
  const [ratingError, setRatingError] = useState<string | null>(null);

  // Reviews / Commissioner Reviews tab state. Commissioner reviews
  // would otherwise get buried below a long list of manager reviews;
  // tabs surface both at the same scroll position.
  const [reviewsTab, setReviewsTab] = useState<0 | 1>(0);

  // Commissioner rating state (separate dialog + separate values).
  // We deliberately do NOT track a league_id here: the backend auto-picks
  // the most recent eligible league the rater hasn't already rated for
  // this commissioner. Asking the rater to choose was friction with no
  // upside.
  const [commishDialogOpen, setCommishDialogOpen] = useState(false);
  const [commishValues, setCommishValues] = useState({
    fairness: 3,
    rule_enforcement: 3,
    responsiveness: 3,
    communication: 3,
    organization: 3,
    review_text: '',
    is_anonymous: false,
  });
  const [commishError, setCommishError] = useState<string | null>(null);

  // Fetch profile
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['sleeperProfile', sleeperUserId],
    queryFn: () => usersApi.getSleeperProfile(sleeperUserId!),
    enabled: !!sleeperUserId,
  });

  // Deep-link auto-open. The Rate Your Leaguemates page (and other
  // "send the user here to rate" entry points) appends `?rate=1` so
  // the dialog opens on its own without the user hunting for the
  // button. We gate on `profile.canRate` so an ineligible user (e.g.
  // already rated, no shared league) doesn't get a confusing dialog
  // they can't submit. The param is consumed once and removed from
  // the URL so a refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('rate') !== '1') return;
    if (!profile) return;
    if (profile.canRate) {
      setRateDialogOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('rate');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, profile]);

  // Fetch ratings
  const { data: ratingsData } = useQuery({
    queryKey: ['accountRatings', profile?.account?.id],
    queryFn: () => ratingsApi.getAccountRatings(profile!.account.id),
    enabled: !!profile?.account?.id,
  });

  // Submit rating mutation
  const submitRatingMutation = useMutation({
    mutationFn: ratingsApi.createRating,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sleeperProfile', sleeperUserId] });
      queryClient.invalidateQueries({ queryKey: ['accountRatings'] });
      setRateDialogOpen(false);
      setRatingError(null);
    },
    onError: (error) => {
      setRatingError(getErrorMessage(error));
    },
  });

  // Commissioner rating queries + mutation.
  const { data: commishData } = useQuery({
    queryKey: ['commissionerRatings', profile?.account?.id],
    queryFn: () => ratingsApi.getCommissionerRatings(profile!.account.id),
    enabled: !!profile?.account?.id,
  });

  const submitCommishMutation = useMutation({
    mutationFn: ratingsApi.createCommissionerRating,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sleeperProfile', sleeperUserId] });
      queryClient.invalidateQueries({ queryKey: ['commissionerRatings'] });
      setCommishDialogOpen(false);
      setCommishError(null);
    },
    onError: (error) => {
      setCommishError(getErrorMessage(error));
    },
  });

  // Admin: Sync leagues mutation
  const syncLeaguesMutation = useMutation({
    mutationFn: () => adminApi.syncSleeperAccountLeagues(profile?.account?.id || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sleeperProfile', sleeperUserId] });
    },
  });

  const handleSubmitRating = () => {
    if (!profile?.account?.id) return;

    submitRatingMutation.mutate({
      rated_sleeper_account_id: profile.account.id,
      payment_reliability: ratingValues.payment_reliability,
      communication: ratingValues.communication,
      fair_play: ratingValues.fair_play,
      league_commitment: ratingValues.league_commitment,
      fair_trader: ratingValues.fair_trader,
      review_text: ratingValues.review_text || undefined,
      is_anonymous: ratingValues.is_anonymous,
    });
  };

  const handleSubmitCommishRating = () => {
    if (!profile?.account?.id) return;
    // No league_id sent — server picks the most recent eligible
    // unrated league for this rater + commissioner pair.
    submitCommishMutation.mutate({
      rated_sleeper_account_id: profile.account.id,
      fairness: commishValues.fairness,
      rule_enforcement: commishValues.rule_enforcement,
      responsiveness: commishValues.responsiveness,
      communication: commishValues.communication,
      organization: commishValues.organization,
      review_text: commishValues.review_text || undefined,
      is_anonymous: commishValues.is_anonymous,
    });
  };

  const openCommishDialog = () => {
    setCommishError(null);
    setCommishDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Alert severity="error">
        User not found or failed to load profile.
      </Alert>
    );
  }

  const {
    account,
    ratingStats,
    commissionerRatingStats,
    usernameHistory,
    avatarHistory: _avatarHistory,
    leagues,
    canRate,
    cannotRateReason,
    canRateAsCommissioner,
    cannotRateCommissionerReason,
    hasSharedIpWarning,
  } = profile;
  // Note: avatarHistory is available but not currently displayed - can be added to UI later

  return (
    <Box>
      {/* Profile Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Identity row: avatar + username/chips on the left, action
              buttons on the right. The display_name line was removed —
              keeping the username as the single visual anchor matches
              how Sleeper displays profiles too. */}
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                src={
                  account.current_avatar_id
                    ? `https://sleepercdn.com/avatars/${account.current_avatar_id}`
                    : undefined
                }
                sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}
              >
                {account.current_username?.[0]?.toUpperCase()}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {account.current_username}
                </Typography>
                {account.is_verified && (
                  <VerifiedIcon sx={{ color: 'success.main' }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {account.is_verified ? (
                  <Chip
                    icon={<VerifiedIcon />}
                    label="Verified Account"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label="Unverified"
                    size="small"
                    variant="outlined"
                  />
                )}
                {hasSharedIpWarning && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Shares IP with other accounts"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                {/* Own verified profile: shortcut to Lineup Help. Shown
                    instead of the rate buttons (you can't rate yourself). */}
                {isAuthenticated && account.is_verified && account.user_id === user?.id && (
                  <Button
                    component={RouterLink}
                    to={`/sleeper/${account.sleeper_user_id}/lineups`}
                    variant="contained"
                    color="secondary"
                    startIcon={<LineupIcon />}
                    sx={{ minWidth: 220, justifyContent: 'flex-start' }}
                  >
                    Check my Lineups
                  </Button>
                )}
                {/* Unverified path: render the rating affordances as
                    disabled-looking buttons that route to verification.
                    Surfaces what the user is missing rather than hiding
                    everything, while keeping a clear single CTA. The
                    server's per-reason `cannotRateReason` strings are
                    intentionally suppressed here — verification is the
                    one and only blocker that matters first.

                    Verified path: render the real Rate buttons below,
                    plus the granular eligibility alerts for cases like
                    "no shared league" or "already rated". */}
                {isAuthenticated && !user?.isVerified && (
                  <>
                    <Tooltip
                      title="Verify your Sleeper account to rate users"
                      arrow
                      placement="left"
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<StarIcon />}
                          disabled
                        >
                          Rate This User
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      component={RouterLink}
                      to="/dashboard?verify=1"
                      size="small"
                      variant="text"
                      startIcon={<VerifiedUserIcon />}
                      sx={{ color: 'primary.main' }}
                    >
                      Verify to rate →
                    </Button>
                  </>
                )}

                {/* Both rate buttons share the same variant/color/size so
                    they read as a unified pair of CTAs rather than a primary
                    + secondary action. */}
                {isAuthenticated && user?.isVerified && canRate && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<StarIcon />}
                    onClick={() => setRateDialogOpen(true)}
                    sx={{ minWidth: 220, justifyContent: 'flex-start' }}
                  >
                    Rate This User
                  </Button>
                )}
                {isAuthenticated && user?.isVerified && canRateAsCommissioner && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CommissionerIcon />}
                    onClick={openCommishDialog}
                    sx={{ minWidth: 220, justifyContent: 'flex-start' }}
                  >
                    Rate as Commissioner
                  </Button>
                )}
                {isAuthenticated && user?.isVerified && !canRate && cannotRateReason && (
                  <Alert severity="info" sx={{ maxWidth: 300 }}>
                    {cannotRateReason}
                  </Alert>
                )}
                {isAuthenticated &&
                  user?.isVerified &&
                  !canRateAsCommissioner &&
                  cannotRateCommissionerReason &&
                  // Only show this hint if it differs from the manager-rating one
                  // (we don't want two almost-identical alerts when the user is
                  // unverified — though that path is now handled separately above).
                  cannotRateCommissionerReason !== cannotRateReason && (
                    <Alert severity="info" sx={{ maxWidth: 300 }} icon={<CommissionerIcon />}>
                      Commish rating: {cannotRateCommissionerReason}
                    </Alert>
                  )}
                {user?.isAdmin && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={syncLeaguesMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={() => syncLeaguesMutation.mutate()}
                    disabled={syncLeaguesMutation.isPending}
                  >
                    {syncLeaguesMutation.isPending ? 'Syncing...' : 'Sync Leagues'}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Reputation row: two distinct cards (Trust + Commissioner) so
          the layout never looks lopsided when one side has no data.
          Each card always renders — the empty side falls back to a
          blurred skeleton with an overlay message. */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <ScoreBlock
                label="Trust Score"
                overall={ratingStats?.overall_average ?? 0}
                total={ratingStats?.total_ratings ?? 0}
                categories={ratingCategories.map((cat) => ({
                  key: cat.key,
                  label: cat.label,
                  value: ratingStats
                    ? (ratingStats[
                        `average_${cat.key}` as keyof typeof ratingStats
                      ] as number)
                    : 0,
                }))}
                isEmpty={!ratingStats}
                emptyMessage={`Be the first to rate ${account.current_username}.`}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <ScoreBlock
                label="Commissioner Score"
                icon={<CommissionerIcon sx={{ color: tokens.legendary }} />}
                overall={commissionerRatingStats?.overall_average ?? 0}
                total={commissionerRatingStats?.total_ratings ?? 0}
                categories={commissionerRatingCategories.map((cat) => ({
                  key: cat.key,
                  label: cat.label,
                  value: commissionerRatingStats
                    ? (commissionerRatingStats[
                        `average_${cat.key}` as keyof typeof commissionerRatingStats
                      ] as number)
                    : 0,
                }))}
                accentColor={tokens.legendary}
                isEmpty={!commissionerRatingStats}
                emptyMessage="Not yet rated as a commissioner."
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Sidebar: leagues table + username history. Replaces the old
            stack of trust-score / commish-score / breakdown cards now
            that those live inside the header above.

            On mobile (xs), the reviews column is visually more important
            than the leagues sidebar, so we re-order so reviews come
            first via Grid `order`. At md+ the original sidebar/main
            layout is restored. */}
        <Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Dynasty Leagues ({leagues.length})
              </Typography>
              {leagues.length > 0 ? (
                <LeaguesTable leagues={leagues} />
              ) : (
                <Typography color="text.secondary">
                  No dynasty leagues tracked
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Username History */}
          {usernameHistory && usernameHistory.length > 1 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <HistoryIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Username History
                  </Typography>
                </Box>
                <List dense>
                  {usernameHistory.map((history, index) => (
                    <ListItem key={history.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={history.username}
                        secondary={`${new Date(history.first_seen_at).toLocaleDateString()} - ${
                          index === 0
                            ? 'Present'
                            : new Date(history.last_seen_at).toLocaleDateString()
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Ratings & Leagues */}
        <Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}>
          {/* Manager + Commissioner reviews share a single tabbed card
              so commissioner reviews don't get buried below a long
              manager-review list. The commish tab only renders when
              there's at least one commish review. When absent, this
              collapses to the original single-titled card. */}
          {(() => {
            const showCommishTab =
              !!commishData?.ratings && commishData.ratings.length > 0;
            const managerCount = ratingsData?.ratings?.length || 0;
            const commishCount = commishData?.ratings?.length || 0;
            const activeTab = showCommishTab ? reviewsTab : 0;
            return (
              <Card sx={{ mb: 3 }}>
                {showCommishTab && (
                  <Tabs
                    value={activeTab}
                    onChange={(_, v) => setReviewsTab(v as 0 | 1)}
                    sx={{
                      px: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Tab label={`Reviews (${managerCount})`} />
                    <Tab
                      label={`Commissioner (${commishCount})`}
                      icon={<CommissionerIcon fontSize="small" />}
                      iconPosition="start"
                      sx={{ minHeight: 'auto' }}
                    />
                  </Tabs>
                )}
                <CardContent>
                  {/* Tab 1: manager reviews (default). */}
                  {activeTab === 0 && (
                    <>
                      {/* Standalone heading only when the tabs aren't
                          there — tabs already convey the section name. */}
                      {!showCommishTab && (
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          Reviews ({managerCount})
                        </Typography>
                      )}
                      {ratingsData?.ratings && ratingsData.ratings.length > 0 ? (
                <List>
                  {ratingsData.ratings.map((rating, index) => (
                    <Box key={rating.id}>
                      {index > 0 && <Divider sx={{ my: 2 }} />}
                      {/* Main Review */}
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              {rating.is_anonymous ? (
                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.600' }}>
                                  <AnonymousIcon sx={{ fontSize: 20 }} />
                                </Avatar>
                              ) : (
                                <Avatar
                                  src={
                                    rating.rater?.sleeperAvatarId
                                      ? `https://sleepercdn.com/avatars/thumbs/${rating.rater.sleeperAvatarId}`
                                      : undefined
                                  }
                                  sx={{ width: 36, height: 36 }}
                                >
                                  {rating.rater?.sleeperUsername?.[0]?.toUpperCase() || '?'}
                                </Avatar>
                              )}
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {rating.is_anonymous ? 'Anonymous' : (rating.rater?.sleeperUsername || 'Unknown')}
                                  </Typography>
                                  {!rating.is_anonymous && rating.rater?.isVerified && (
                                    <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                  )}
                                  {rating.isVerifiedLeaguemate ? (
                                    <Chip
                                      label="Verified Leaguemate"
                                      size="small"
                                      color="success"
                                      sx={{ 
                                        height: 20, 
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Unverified Leaguemate"
                                      size="small"
                                      variant="outlined"
                                      sx={{ 
                                        height: 20, 
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 1 },
                                        borderColor: 'text.secondary',
                                        color: 'text.secondary',
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(rating.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box>
                                <Rating
                                  value={
                                    (rating.payment_reliability +
                                      rating.communication +
                                      rating.fair_play +
                                      rating.league_commitment +
                                      rating.fair_trader) /
                                    5
                                  }
                                  precision={0.1}
                                  readOnly
                                  size="small"
                                />
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                            >
                              {rating.review_text || 'No written review'}
                            </Typography>
                          }
                        />
                      </ListItem>

                      {/* Replies */}
                      {rating.replies && rating.replies.length > 0 && (
                        <Box sx={{ ml: 6, mt: 1 }}>
                          {rating.replies.map((reply) => (
                            <Box
                              key={reply.id}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                borderLeft: 3,
                                borderColor: 'primary.main',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Avatar
                                  src={
                                    reply.author?.sleeperAvatarId
                                      ? `https://sleepercdn.com/avatars/thumbs/${reply.author.sleeperAvatarId}`
                                      : undefined
                                  }
                                  sx={{ width: 24, height: 24 }}
                                >
                                  {reply.author?.sleeperUsername?.[0]?.toUpperCase() || '?'}
                                </Avatar>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {reply.author?.sleeperUsername || 'Anonymous'}
                                </Typography>
                                {reply.author?.isVerified && (
                                  <VerifiedIcon sx={{ fontSize: 12, color: 'success.main' }} />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(reply.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {reply.reply_text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Reply Button */}
                      {isAuthenticated && (
                        <Box sx={{ ml: 6, mt: 1 }}>
                          <ReplyForm
                            ratingId={rating.id}
                            onSuccess={() => {
                              queryClient.invalidateQueries({ queryKey: ['accountRatings'] });
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  No reviews yet. Be the first to rate this user!
                </Typography>
              )}
                    </>
                  )}
                  {/* Tab 2: commissioner reviews. */}
                  {activeTab === 1 && (
                    <List sx={{ pt: 0 }}>
                  {(commishData?.ratings ?? []).map((rating, index) => {
                    const avg =
                      (rating.fairness +
                        rating.rule_enforcement +
                        rating.responsiveness +
                        rating.communication +
                        rating.organization) /
                      5;
                    return (
                      <Box key={rating.id}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {rating.is_anonymous ? (
                                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.600' }}>
                                    <AnonymousIcon sx={{ fontSize: 20 }} />
                                  </Avatar>
                                ) : (
                                  <Avatar
                                    src={
                                      rating.rater?.sleeperAvatarId
                                        ? `https://sleepercdn.com/avatars/thumbs/${rating.rater.sleeperAvatarId}`
                                        : undefined
                                    }
                                    sx={{ width: 36, height: 36 }}
                                  >
                                    {rating.rater?.sleeperUsername?.[0]?.toUpperCase() || '?'}
                                  </Avatar>
                                )}
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {rating.is_anonymous
                                        ? 'Anonymous'
                                        : rating.rater?.sleeperUsername || 'Unknown'}
                                    </Typography>
                                    {!rating.is_anonymous && rating.rater?.isVerified && (
                                      <VerifiedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    )}
                                    {rating.league && (
                                      <Chip
                                        label={`${rating.league.name} (${rating.league.season})`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.7rem',
                                          '& .MuiChip-label': { px: 1 },
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(rating.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                <Box>
                                  {/* Match the legendary accent used by
                                      the Commissioner Score block in the
                                      header so commish ratings read in a
                                      consistent hue throughout the page.
                                      Targets the iconFilled slot directly
                                      because the theme has a global
                                      override on it. */}
                                  <Rating
                                    value={avg}
                                    precision={0.1}
                                    readOnly
                                    size="small"
                                    sx={{
                                      '& .MuiRating-iconFilled': {
                                        color: tokens.legendary,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                              >
                                {rating.review_text || 'No written review'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </Box>
                    );
                  })}
                    </List>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* League sections used to render here as cards in the right
              column. They've been moved into a compact table in the
              left sidebar (above) — see <LeaguesTable />. */}
        </Grid>
      </Grid>

      {/* Rate Dialog */}
      <Dialog
        open={rateDialogOpen}
        onClose={() => setRateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rate {account.current_username}</DialogTitle>
        <DialogContent>
          {ratingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ratingError}
            </Alert>
          )}
          <Stack spacing={3} sx={{ mt: 1 }}>
            {ratingCategories.map((cat) => (
              <Box key={cat.key}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {cat.label}
                </Typography>
                <Rating
                  value={ratingValues[cat.key as keyof typeof ratingValues] as number}
                  onChange={(_, newValue) =>
                    setRatingValues((prev) => ({
                      ...prev,
                      [cat.key]: newValue || 1,
                    }))
                  }
                  size="large"
                />
              </Box>
            ))}
            <TextField
              label="Review (optional)"
              multiline
              rows={3}
              value={ratingValues.review_text}
              onChange={(e) =>
                setRatingValues((prev) => ({
                  ...prev,
                  review_text: e.target.value,
                }))
              }
              placeholder="Share your experience with this user..."
            />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
            }}>
              <Box>
                <Typography variant="subtitle2">
                  Post Anonymously
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Your identity will be hidden, but "Verified Leaguemate" badge will still show
                </Typography>
              </Box>
              <Tooltip title="Hide your identity to avoid drama with leaguemates">
                <Switch
                  checked={ratingValues.is_anonymous}
                  onChange={(e) =>
                    setRatingValues((prev) => ({
                      ...prev,
                      is_anonymous: e.target.checked,
                    }))
                  }
                />
              </Tooltip>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitRating}
            variant="contained"
            color="secondary"
            disabled={submitRatingMutation.isPending}
          >
            {submitRatingMutation.isPending ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commissioner Rating Dialog */}
      <Dialog
        open={commishDialogOpen}
        onClose={() => setCommishDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommissionerIcon sx={{ color: tokens.legendary }} />
            Rate {account.current_username} as Commissioner
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These ratings are about how they ran your league — not how they
            played in it. You can only rate someone as a commissioner if you
            were in a league where they were the commissioner.
          </Typography>

          {commishError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {commishError}
            </Alert>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            {commissionerRatingCategories.map((cat) => (
              <Box key={cat.key}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {cat.label}
                </Typography>
                {/* Legendary tint here too — same accent the user will
                    see on the rendered review afterwards, so the rating
                    they're picking matches its eventual display.
                    Targets the iconFilled / iconHover slots directly to
                    beat the theme's global gold override. */}
                <Rating
                  value={commishValues[cat.key as keyof typeof commishValues] as number}
                  onChange={(_, newValue) =>
                    setCommishValues((prev) => ({
                      ...prev,
                      [cat.key]: newValue || 3,
                    }))
                  }
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': { color: tokens.legendary },
                    '& .MuiRating-iconHover': { color: tokens.legendaryGlow },
                  }}
                />
              </Box>
            ))}

            <TextField
              label="Review (optional)"
              multiline
              rows={3}
              value={commishValues.review_text}
              onChange={(e) =>
                setCommishValues((prev) => ({ ...prev, review_text: e.target.value }))
              }
              placeholder="How did they handle disputes, communication, setup, etc.?"
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2">Post Anonymously</Typography>
                <Typography variant="caption" color="text.secondary">
                  Hides your identity on this review
                </Typography>
              </Box>
              <Switch
                checked={commishValues.is_anonymous}
                onChange={(e) =>
                  setCommishValues((prev) => ({
                    ...prev,
                    is_anonymous: e.target.checked,
                  }))
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommishDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitCommishRating}
            variant="contained"
            color="secondary"
            disabled={submitCommishMutation.isPending}
          >
            {submitCommishMutation.isPending ? 'Submitting...' : 'Submit Commish Rating'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

